import { Button } from "@/components/ui/baseUi/button"
import { successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { GlobeIcon } from "@images/svgIcons/globeIcon"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { LockIcon } from "@images/svgIcons/lockIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { ProLargeIcon } from "@images/svgIcons/proLargeIcon"
import { SubscribersIcon } from "@images/svgIcons/subscribersIcon"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramStatus, ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import {
  getDisplayStatus,
  getDisplayVisibility,
  getDurationText,
  getSubscriptionCountText,
} from "@modules/programs/utils/programUtils"
import { Calendar, ChevronLeft, Eye, EyeOff, MessageCircleMore, Settings, Share } from "lucide-react"
import { Link } from "react-router"

type ProgramInformationProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramInformation({ programId, className, ...props }: ProgramInformationProps) {
  const { data: program } = useProgram(programId)

  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const programSubscribersText = getSubscriptionCountText(
    program.subscriptionCount,
    isAuthor,
    isAcceptedSharedCoach,
    true,
    program.type,
  )
  const programDurationText = "duration" in program && program.duration ? getDurationText(program.duration) : ""
  const mySpaceNavigate = useMySpaceNavigate()
  const { navigateToProgramView } = useProgramSidebar()
  const navigate = useMySpaceNavigate()
  const onViewDetailsProgramClick = () => {
    navigateToProgramView(program.id, true)
  }

  const canUserChatWithMembers =
    program && program.type !== ProgramType.ONOFF && (isAuthor || isAcceptedSharedCoach || program.subscribed)

  const openChatWithProgramGroup = () => {
    if (canUserChatWithMembers) {
      if (program.id) {
        navigate(Path.Chat.Root, { state: { programToChat: program.id } })
      }
    }
  }

  const canUserShareInvitationLink =
    program &&
    program.invitationLink &&
    (program.type === ProgramType.VIRTUAL || program.status === ProgramStatus.PUBLISHED) &&
    (program.visibility === ProgramVisibility.PUBLIC ||
      (program.visibility === ProgramVisibility.PRIVATE && (isAuthor || isAcceptedSharedCoach)))

  const onCopyInvitationLink = () => {
    if (canUserShareInvitationLink && program.invitationLink) {
      navigator.clipboard.writeText(program.invitationLink)
      toast.success(word("program.details.share.success"), { classNames: successToastClassName() })
    }
  }

  return (
    <div className={cn("flex w-full flex-row p-5", className)} {...props}>
      {/* Left side */}
      <div className="flex w-1/2 flex-col justify-between">
        <div className="flex flex-col">
          <div className="mb-2 flex flex-row items-center gap-2">
            {program.type === ProgramType.VIRTUAL ? (
              <InfinityIcon color="currentColor" width={24} height={24} className="shrink-0 text-textOnColor-heading" />
            ) : program.type === ProgramType.PRO ? (
              <ProLargeIcon color="currentColor" width={24} height={24} className="shrink-0 text-textOnColor-heading" />
            ) : (
              <OnOffIcon color="currentColor" width={24} height={24} className="shrink-0 text-textOnColor-heading" />
            )}
            <Text color="currentColor" fontStyle="TL" className="line-clamp-1 text-textOnColor-heading">
              {program.name}
            </Text>
          </div>

          <div className="flex flex-row items-center gap-1">
            {program.visibility === ProgramVisibility.PUBLIC ? (
              <GlobeIcon color="currentColor" width={18} height={18} className="text-primary-500" />
            ) : (
              <LockIcon color="currentColor" width={18} height={18} className="text-secondary-200" />
            )}
            <Text fontStyle="BM" className="ml-2 text-secondary-200">
              {getDisplayVisibility(program.visibility)}{" "}
              {program.type === ProgramType.ONOFF &&
                program.status === ProgramStatus.DRAFT &&
                `(${getDisplayStatus(program.status).toLowerCase()})`}
            </Text>
          </div>

          {program.type === ProgramType.ONOFF && program.duration && (
            <div className="flex items-center gap-1">
              <Calendar color="currentColor" width={18} height={18} className="text-secondary-200" />
              <Text fontStyle="BM" className="ml-2 text-secondary-200">
                {programDurationText}
              </Text>
            </div>
          )}
          <div className="flex flex-row items-center">
            <SubscribersIcon color="currentColor" width={18} height={18} className="text-secondary-200" />
            <Text fontStyle="BM" className="ml-2 text-secondary-200">
              {programSubscribersText}
            </Text>
          </div>
          <div className="flex flex-row items-center">
            {program.defaultHideSessionContent ? (
              <EyeOff color="currentColor" width={18} height={18} className="text-secondary-200" />
            ) : (
              <Eye color="currentColor" width={18} height={18} className="text-secondary-200" />
            )}

            <Text fontStyle="BM" className="ml-2 text-secondary-200">
              {word("program.details.hideSessionContent", {
                contentHidden: program.defaultHideSessionContent
                  ? word("program.details.sessionContent.hidden")
                  : word("program.details.sessionContent.apparent"),
              })}
            </Text>
          </div>
        </div>
        <Link
          to={Path.MySpace.Training}
          onClick={(e) => {
            e.preventDefault()
            mySpaceNavigate(Path.MySpace.Training)
          }}
        >
          <Button variant="underline" className="text-secondary-200">
            <ChevronLeft />
            {word("program.returnToTraining")}
          </Button>
        </Link>
      </div>

      {/* Right side */}
      <div className="flex w-1/2 flex-col justify-between border-l border-l-borderDefault/80">
        <div className="ml-2 flex flex-row items-center gap-2">
          <ProfileAvatar
            profile={{ avatar: program.owner?.avatar ?? null, username: program.owner?.firstname ?? "" }}
          />
          <div className="flex flex-col overflow-hidden text-left">
            <Text
              fontStyle="LL"
              className="overflow-hidden overflow-ellipsis text-textOnColor-heading"
            >{`${program.owner?.firstname ?? ""} ${program.owner?.lastname ?? ""}`}</Text>
            <Text fontStyle="BS" className="text-secondary-200">
              {word("program.label.author")}
            </Text>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          {canUserChatWithMembers && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="outline" size="iconMd" onClick={openChatWithProgramGroup}>
                    <MessageCircleMore color="currentColor" size={16} />
                  </Button>
                }
              />
              <TooltipContent side="bottom">
                <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                  {word("chat.button.goToProgramChat")}
                </Text>
              </TooltipContent>
            </Tooltip>
          )}
          {canUserShareInvitationLink && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="outline" size="iconMd" onClick={onCopyInvitationLink}>
                    <Share color="currentColor" size={16} />
                  </Button>
                }
              />
              <TooltipContent side="bottom">
                <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                  {word("sidebar.button.dynamicLink")}
                </Text>
              </TooltipContent>
            </Tooltip>
          )}
          {(isAuthor || isAcceptedSharedCoach) && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="primary" size="iconMd" onClick={onViewDetailsProgramClick}>
                    <Settings color="currentColor" size={16} />
                  </Button>
                }
              />
              <TooltipContent side="bottom">
                <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                  {word("tooltip.settings")}
                </Text>
              </TooltipContent>
            </Tooltip>
          )}
          {!(isAuthor || isAcceptedSharedCoach) && (
            <Button variant="outline" onClick={onViewDetailsProgramClick}>
              {word("program.seeInformation")}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
